import { Decorative } from "@faire/design-tokens";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { QueryParameters as SignUpQueryParameters } from "@faire/web-api/routes/direct/brandAlias";
import { useStrictLocalization } from "@faire/web/common/localization";
import { getCurrentPath } from "@faire/web/common/Path";
import { AlertBanner } from "@faire/web/ui/AlertBanner";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import React, { Suspense } from "react";
import { useHistory } from "react-router-dom";

import { SAMPLE_PROSPECTING_SAMPLE_REQUEST_COMPLETE_VALUE } from "@faire/retailer/components/App/AppModals/consts";
import { LazySampleRequestInitiationModal } from "@faire/retailer/features/ProspectingWithSamples/LazySampleRequestInitiationModal";

interface ProspectingSampleBannerProps {
  isPreSample: boolean;
  brand: IBrand;
}

/*
 * This component contains a banner and a modal that lets users request samples from brands (Brand Prospecting).
 * Banner and modals are all on top of Faire Direct landing page.
 */
export const ProspectingSampleBannerAndModals: React.FC<
  ProspectingSampleBannerProps
> = ({ isPreSample, brand }) => {
  const { strictLocalize } = useStrictLocalization();
  const { push } = useHistory();

  const parser = SignUpQueryParameters.parse;
  const { signUp } = useQueryParams(parser);

  const newToSamples =
    signUp !== SAMPLE_PROSPECTING_SAMPLE_REQUEST_COMPLETE_VALUE;
  const [isSampleInitialModalOpen, setIsSampleInitialModalOpen] =
    React.useState(newToSamples);

  if (!isPreSample) {
    return null;
  }

  const closeSampleRequestEmailModal = () => {
    setIsSampleInitialModalOpen(false);
  };

  const emailSubmitted = (email: string) => {
    push(getCurrentPath().append({ email }).toString());
  };

  return (
    <>
      {newToSamples ? (
        <AlertBanner
          message={strictLocalize({
            defaultMessage: "Want to taste first?",
            description: {
              text: "Text in the banner to encourage userrs to sample that brand",
              img: "https://cdn.faire.com/fastly/7452543aeff55c73ec487ed3cb11691e718d91794444c2f51b02881e5ed2744e.png",
            },
          })}
          alertAction={{
            text: strictLocalize({
              defaultMessage: "Get a free sample",
              description: {
                text: "A link on the banner that opens a modal through which user can request a sample",
                img: "https://cdn.faire.com/fastly/7452543aeff55c73ec487ed3cb11691e718d91794444c2f51b02881e5ed2744e.png",
              },
            }),
            action: { onClick: () => setIsSampleInitialModalOpen(true) },
          }}
          color={Decorative.tint.green}
        />
      ) : null}
      <Suspense fallback={null}>
        <LazySampleRequestInitiationModal
          isOpen={isSampleInitialModalOpen}
          brand={brand}
          onClose={closeSampleRequestEmailModal}
          onSubmitEmail={emailSubmitted}
        />
      </Suspense>
    </>
  );
};
